import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import MessagesListContainer from 'supportAssistant/components/Chat/MessagesListContainer'
import MessageInput from 'supportAssistant/components/Chat/MessageInput'
import Header from 'supportAssistant/components/Chat/Header'
import AssistChip from 'components/Luxkit/Chips/AssistChip'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import ModalContext from 'contexts/ModalContext'
import FeedbackModal from 'supportAssistant/components/Chat/FeedbackModal'
import { CHAT, ChatActions, getWelcomeMessage, MESSAGE, postFeedback } from 'api/support'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import TextLink from 'components/Luxkit/TextLink'
import DisclaimerModal from 'supportAssistant/components/Chat/DisclaimerModal'
import { shallowEqual } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { Agent } from 'supportAssistant/components/Support/Agent'

import useSocketEvent from '../../hooks/useSocketEvent'
import { chatActions } from 'supportAssistant/constants/chatActions'
import { useOrdersByDepartureStatus } from 'hooks/useOrdersByDepartureStatus'
import MetaComponents from './MessageMeta/MetaComponents'
import { uniqueBy } from 'lib/array/arrayUtils'
import uuidV4 from 'lib/string/uuidV4Utils'
import HeadData from 'components/Common/HeadData'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { Chat as ChatType, ChatStatus, ContentTypes, ChatMessage, MessageSenders } from 'api/support'

const StyledContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr min-content auto;
  grid-template-areas:
    "messages"
    "miscellaneous"
    "messageInput"
    "disclaimer";
  height: 100%;
  width: 100%;
  position: relative;
  padding: ${rem(24)};
  padding-top: ${rem(60)};
  background-color: ${({ theme }) => theme.palette.neutral.default.eight};
`

const HeaderContainer = styled.div`
  width: 100%;
  position: absolute;
  top: 0;
`

const StyledMessagesListContainer = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  grid-area: messages;
  overflow-x: auto;
  height: 100%;
  margin-bottom: ${rem(4)};
  background-color: ${({ theme }) => theme.palette.neutral.default.eight};

  &::-webkit-scrollbar {
    display: none;
  }

  &--scroll-container {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }
`

const ActionsContainer = styled.div`
  display: grid;
  justify-items: end;
  grid-gap: ${rem(8)};
  margin-top: ${rem(12)};
`

const ActionsSkeleton = styled(LoadingBox)`
  align-self: end;
  border-radius: ${props => props.theme.borderRadius.round};
  height: ${rem(32)};
`

const MessageInputContainer = styled.div`
  grid-area: messageInput;
  margin-bottom: ${rem(6)};
`

const ChatClosedBodyTextBlock = styled(BodyTextBlock)`
  margin-bottom: ${rem(56)};
`

const ChatClosedDiv = styled.div`
  grid-area: miscellaneous;
  display: flex;
  margin-bottom: ${rem(12)};
  flex-direction: column;
`

const NewChatAssistChip = styled(AssistChip)`
  align-self: end;
`

const MessagesInputSkeleton = styled(LoadingBox)`
  grid-area: messageInput;
  height: ${rem(46)};
  width: 100%;
  border-radius: ${props => props.theme.borderRadius.S};
`

const DisclaimerContainer = styled.div`
  grid-area: disclaimer;
`

const StyledDisclaimerTextBlock = styled(BodyTextBlock)`
  margin-top: ${rem(12)};
  margin-bottom: ${rem(4)};
  justify-content: center;
  width: 100%;
  ${mediaQueryUp.desktop} {
    justify-content: start;
  }
`

const CLEAR_OFFER_ACTION_CONTENT = { text: 'I am not interested in this offer', type: ContentTypes.BUTTON, action: { key: ChatActions.CLEAR_OFFER_FROM_CONTEXT } }

const ScrollTo = styled.div`
  padding-bottom: ${rem(4)};
`

type MessageExtended = ChatMessage & { renderId?: string }

interface ChatProps {
  userId?: string;
  activeChat?: ChatType;
  onNewChat: () => void;
  onSelectChat:(chatId: string) => void;
  toggleHistory?: () => void;
  loadingMessages?: boolean;
}

function Chat({
  activeChat,
  onNewChat,
  onSelectChat,
  toggleHistory,
  userId,
  loadingMessages,
}: React.PropsWithChildren<ChatProps>) {
  const dispatch = useAppDispatch()

  const [messages, setMessages] = useState<MessageExtended[]>([])
  const [queuedMessage, setQueuedMessage] = useState<any>(null)
  const [outageMessage, setOutageMessage] = useState<string | undefined>(undefined)
  const [typingState, setTypingState] = useState<Record<string, boolean>>({})
  const [welcomeMessage, setWelcomeMessage] = useState<ChatMessage>()
  const [showDocumentTitleMessage, setShowDocumentTitleMessage] = useState(false)
  const [isTabVisible, setIsTabVisible] = useState(true)

  const lastMessage = activeChat ? messages[messages.length - 1] : welcomeMessage
  const isLastMessageByUser = lastMessage?.sender === MessageSenders.USER
  const isTyping = !loadingMessages && activeChat && typingState[activeChat.id] || false

  // getting action that is not of type button, to attach it on message when we just send it through messageInput
  const lastMessageMetaInputAction = lastMessage?.meta?.content?.find((meta) => meta.type === ContentTypes.INPUT)
  const metaPayload = lastMessage?.meta?.payload
  const metaContent = useMemo(() => {
    if (metaPayload?.offerId) {
      return [CLEAR_OFFER_ACTION_CONTENT, ...(lastMessage?.meta?.content || [])]
    }

    return lastMessage?.meta?.content
  }, [lastMessage, metaPayload?.offerId])

  const email = useAppSelector(state => state.auth.account?.email)
  const phone = useAppSelector(state => state.auth.account?.phone)
  const [firstName, lastName] = useAppSelector((state: App.State) => [state.auth.account.givenName, state.auth.account.surname], shallowEqual)

  const createChat = useSocketEvent(CHAT.CREATE)
  const sendMessage = useSocketEvent(MESSAGE.MESSAGE)
  const sendFeedback = useSocketEvent(MESSAGE.FEEDBACK)

  const scrollRef = useRef<HTMLInputElement>(null)

  const scrollToBottom = () => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' })
  }

  useEffect(() => {
    scrollToBottom()
  })

  useEffect(() => {
    getWelcomeMessage().then(setWelcomeMessage)
  }, [])

  useEffect(() => {
    setMessages(activeChat?.messages || [])
  }, [activeChat])

  // first message, when chat history is empty and there
  // is no active chat, creates new chat and gets sent to the server
  useEffect(() => {
    if (activeChat?.id && queuedMessage) {
      sendMessage({
        ...queuedMessage,
        chatId: activeChat?.id,
      })

      setMessages((prevMessages) => [...prevMessages, { ...queuedMessage, sender: MessageSenders.USER }])
      setQueuedMessage(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat?.id, createChat, queuedMessage, userId])

  useEffect(() => {
    document.addEventListener('visibilitychange', () => setIsTabVisible(document.visibilityState === 'visible'))
    return () => {
      document.removeEventListener('visibilitychange', () => setIsTabVisible(document.visibilityState === 'visible'))
    }
  }, [])

  useEffect(() => {
    if (isLastMessageByUser || isTabVisible) {
      setShowDocumentTitleMessage(false)
    }
  }, [isLastMessageByUser, isTabVisible, messages])

  const onMessageResponse = useCallback((newMessage) => {
    setTypingState(prevState => ({ ...prevState, [newMessage.chatId]: false }))
    setShowDocumentTitleMessage(true)

    if (newMessage.chatId === activeChat?.id) { // The new message came to the active chat
      // in some cases welcome message was duplicated because of race condition
      setMessages((prevMessages) => uniqueBy([...prevMessages, newMessage], message => message.id || message.renderId))
    }
  }, [activeChat?.id])

  useSocketEvent(MESSAGE.RESPONSE, onMessageResponse)

  const onMessageTyping = useCallback(() => {
    if (activeChat) {
      setTypingState({ ...typingState, [activeChat?.id]: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat?.id, typingState])

  useSocketEvent(MESSAGE.TYPING, onMessageTyping)

  const onChatErrorService = useCallback((error) => {
    if (activeChat) {
      setTypingState({ ...typingState, [activeChat?.id]: false })
      setOutageMessage(error.text)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat?.id, typingState])

  useSocketEvent(CHAT.ERROR_SERVICE, onChatErrorService)

  const createMessage = useCallback((message: string, meta?: any) => {
    const trimmedMessage = message.trim()

    if (!trimmedMessage && !meta) {
      return null
    }

    const messageMeta = meta ? {
      action: meta.key,
      payload: {
        email,
        phone,
        firstName,
        lastName,
        chatId: activeChat?.id,
        customerId: userId,
        ...meta.payload,
      },
    } : {
      payload: { customerId: userId }, // for intent detection purposes
    }

    const messageAction = chatActions[messageMeta.action]

    if (messageAction) {
      messageAction?.(messageMeta)
    }

    return {
      text: trimmedMessage,
      chatId: activeChat?.id,
      customer: {
        firstName,
        lastName,
      },
      meta: messageMeta,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, chatActions, dispatch, activeChat?.id, firstName, lastName])

  const onMessageSend = useCallback((message: string, system: boolean = false) => {
    // attaching action to message if not sent by clicking action button
    // required for cases like resetPasswordRequestEmail
    const meta = lastMessageMetaInputAction ?
        {
          key: lastMessageMetaInputAction.action?.key,
          payload: { ...lastMessage?.meta?.payload, inputPayload: message },
        } : {
          payload: lastMessage?.meta?.payload,
        }

    const messageData = createMessage(message, meta)

    if (!messageData) {
      return
    }

    // first message, when chat history is empty and there
    // is no active chat, should create new chat
    if (!activeChat?.id) {
      createChat({
        customerId: userId,
      })
      setQueuedMessage(messageData)
      return
    }
    const sender = system ? MessageSenders.SYSTEM : MessageSenders.USER
    setMessages((prevMessages) => [...prevMessages, {
      ...messageData,
      renderId: uuidV4(), // Add a unique id to the message to avoid duplicates
      sender,
    }])

    sendMessage({ ...messageData, sender })
  }, [lastMessageMetaInputAction, createMessage, activeChat?.id, sendMessage, createChat, userId, lastMessage?.meta?.payload])

  const onMetaMessageSend = useCallback((message: string, metaMessage: any) => {
    const messageData = createMessage(message, metaMessage)

    if (!messageData) {
      return
    }

    // first message, when chat history is empty and there
    // is no active chat, should create new chat
    if (!activeChat?.id) {
      createChat({
        customerId: userId,
      })
      setQueuedMessage(messageData)
      return
    }

    const sender = MessageSenders.USER

    setMessages((prevMessages) => [...prevMessages, { ...messageData, sender }])

    sendMessage({ ...messageData, sender })
  }, [activeChat, createChat, createMessage, sendMessage, userId])

  const showModal = useContext(ModalContext)

  const submitMessageFeedback = useCallback((feedback) => {
    sendFeedback(feedback)
  }, [sendFeedback])

  const submitFeedback = useCallback((feedback) => {
    postFeedback({
      chatId: activeChat?.id,
      customerId: userId,
      ...feedback,
    })
      .then(() => showSnackbar(
        'Thank you for your valuable feedback. It greatly helps us improve the user experience.',
        'success',
        { heading: 'Feedback successfully submitted' },
      ))
      .catch(() => showSnackbar(
        'Something went wrong. Please try again later.',
        'warning',
        { heading: 'Error' },
      ))
  }, [activeChat?.id, userId])

  const toggleFeedbackModal = useCallback(async() => {
    const feedback = await showModal<undefined | any>(<FeedbackModal />)

    if (feedback.issues?.length) {
      submitFeedback(feedback)
    }
  }, [showModal, submitFeedback])

  const toggleDisclaimerModal = useCallback(() => {
    showModal<undefined | any>(<DisclaimerModal />)
  }, [showModal])

  const onMessageFeedbackSubmit = useCallback(async(userFeedback) => {
    const messageFeedback = {
      feedback: userFeedback.sentiment,
      chatId: activeChat?.id,
      messageId: userFeedback.messageId,
    }

    submitMessageFeedback(messageFeedback)
    submitFeedback(userFeedback)
  }, [activeChat?.id, submitFeedback, submitMessageFeedback])

  const customerOrdersData = useOrdersByDepartureStatus()

  return (
    <StyledContainer>
      {showDocumentTitleMessage && <HeadData defer={false} title="(1) New Message - Luxury Escapes" />}
      <HeaderContainer>
        <Header
          toggleHistory={toggleHistory}
          toggleFeedback={toggleFeedbackModal}
          disableReportFeedback={!activeChat}
          onNewChat={onNewChat}
        />
      </HeaderContainer>
      <StyledMessagesListContainer>
        <MessagesListContainer
          loadingMessages={loadingMessages}
          messages={messages}
          welcomeMessagePlaceholder={welcomeMessage}
          isTyping={isTyping}
          chatCreatedAt={activeChat?.createdAt}
          onMessageFeedbackSubmit={onMessageFeedbackSubmit}
        />
        <ActionsContainer>
          <Agent
            activeChat={activeChat}
            onSelectChat={onSelectChat}
            outageMessage={outageMessage}
            onResetOutageMessage={() => setOutageMessage(undefined)}
            customer={{
              firstName,
              lastName,
            }}
            loadingMessages={loadingMessages}
          />
          {loadingMessages &&
            <ActionsSkeleton style={{ width: 220 }} />
          }
          {!loadingMessages && activeChat?.status !== ChatStatus.CLOSED && lastMessage?.sender === MessageSenders.CHATBOT && metaContent &&
            <MetaComponents metaContent={metaContent} payload={metaPayload} handleAction={onMetaMessageSend} customerOrdersData={customerOrdersData} />
          }
        </ActionsContainer>
        <ScrollTo ref={scrollRef} />
      </StyledMessagesListContainer>

      {!loadingMessages && activeChat?.status === ChatStatus.CLOSED && (
        <ChatClosedDiv>
          <ChatClosedBodyTextBlock variant="small" align="center">
            This chat is timed out. Please start a new chat.
          </ChatClosedBodyTextBlock>
          <NewChatAssistChip variant="filled" size="medium" onClick={onNewChat}>
            Start new chat
          </NewChatAssistChip>
        </ChatClosedDiv>
      )}

      {!loadingMessages && outageMessage && (
        <ChatClosedDiv>
          <MessageBanner
            description={outageMessage}
            heading=""
            kind="critical"
          />
        </ChatClosedDiv>
      )}

      {loadingMessages && <MessagesInputSkeleton />}
      {!loadingMessages && (
        <MessageInputContainer>
          <MessageInput
            onMessageSend={onMessageSend}
            isTyping={isTyping}
            chatStatus={activeChat?.status}
            chatAgentType={activeChat?.agentType}
            isLastMessageByUser={isLastMessageByUser}
            isDown={!!outageMessage}
            lastMessageMetaInputAction={lastMessageMetaInputAction}
          />
        </MessageInputContainer>
      )}<DisclaimerContainer>
        <StyledDisclaimerTextBlock variant="small">
          Information provided is general in nature and may be inaccurate. Do not send private or sensitive information. {' '}
          <TextLink
            weight="regular"
            onClick={toggleDisclaimerModal}
          >
            Find out more
          </TextLink>
        </StyledDisclaimerTextBlock>
      </DisclaimerContainer>
    </StyledContainer>
  )
}

export default Chat
